import React from 'react';
import {
  PhotoIcon,
  CheckCircleIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

interface MediaItem {
  id: number;
  uuid: string;
  filename: string;
  title: string;
  caption?: string;
  alt_text?: string;
  category: string;
  folder_id?: number;
  tags?: string[];
  original_path: string;
  optimized_path?: string;
  thumbnail_path?: string;
  width: number;
  height: number;
  original_size: number;
  uploaded_at: string;
  usage_count: number;
}

interface MediaGalleryProps {
  items: MediaItem[];
  viewMode: 'grid' | 'list';
  selectedItems: number[];
  onSelectItem: (id: number) => void;
  onViewDetails: (item: MediaItem) => void;
}

export default function MediaGallery({
  items,
  viewMode,
  selectedItems,
  onSelectItem,
  onViewDetails
}: MediaGalleryProps) {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const hours = Math.floor(diffTime / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diffTime / (1000 * 60));
        return `${minutes} min ago`;
      }
      return `${hours} hours ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'map':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
      case 'diagram':
        return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
      case 'news':
        return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400';
      case 'quiz':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
            <tr>
              <th className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      items.forEach(item => {
                        if (!selectedItems.includes(item.id)) {
                          onSelectItem(item.id);
                        }
                      });
                    } else {
                      items.forEach(item => {
                        if (selectedItems.includes(item.id)) {
                          onSelectItem(item.id);
                        }
                      });
                    }
                  }}
                  checked={items.length > 0 && items.every(item => selectedItems.includes(item.id))}
                  className="rounded"
                />
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Preview
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Name
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Category
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Size
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Dimensions
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Uploaded
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {items.map((item) => (
              <tr
                key={item.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                onClick={() => onViewDetails(item)}
              >
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item.id)}
                    onChange={() => onSelectItem(item.id)}
                    className="rounded"
                  />
                </td>
                <td className="px-4 py-3">
                  {item.thumbnail_path || item.optimized_path ? (
                    <img
                      src={item.thumbnail_path || `/media/file/${item.uuid}/thumbnail`}
                      alt={item.alt_text || item.title}
                      className="w-12 h-12 object-cover rounded"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-600 rounded flex items-center justify-center">
                      <DocumentIcon className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {item.title}
                    </p>
                    {item.caption && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {item.caption}
                      </p>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(item.category)}`}>
                    {item.category}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {formatFileSize(item.original_size)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {item.width} × {item.height}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(item.uploaded_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {items.map((item) => {
        const isSelected = selectedItems.includes(item.id);

        return (
          <div
            key={item.id}
            className={`
              relative group cursor-pointer rounded-lg overflow-hidden
              border-2 transition-all
              ${isSelected
                ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }
            `}
            onClick={() => onViewDetails(item)}
          >
            {/* Selection Checkbox */}
            <div
              className={`
                absolute top-2 left-2 z-10
                ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                transition-opacity
              `}
              onClick={(e) => {
                e.stopPropagation();
                onSelectItem(item.id);
              }}
            >
              {isSelected ? (
                <CheckCircleSolid className="w-6 h-6 text-blue-500" />
              ) : (
                <div className="w-6 h-6 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-full hover:border-blue-500" />
              )}
            </div>

            {/* Usage Badge */}
            {item.usage_count > 0 && (
              <div className="absolute top-2 right-2 z-10 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                {item.usage_count} uses
              </div>
            )}

            {/* Image */}
            <div className="aspect-square bg-gray-100 dark:bg-gray-800">
              {item.thumbnail_path || item.optimized_path ? (
                <img
                  src={item.thumbnail_path || `/media/file/${item.uuid}/thumbnail`}
                  alt={item.alt_text || item.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <PhotoIcon className="w-12 h-12 text-gray-400" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="p-2 bg-white dark:bg-gray-800">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {item.title}
              </p>
              <div className="flex items-center justify-between mt-1">
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${getCategoryColor(item.category)}`}>
                  {item.category}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatFileSize(item.original_size)}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}